import * as React from 'react';
import { Loader2, Save, Plus, Trash2, Inbox } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input, Textarea } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useToast } from '../../../components/ui/toast';
import {
  useTicketSettings,
  useUpdateTicketSettings,
} from '../../../hooks/useTicketSettings';
import { NotificationTriggerCard } from '../../../components/NotificationTriggerCard';
import { ZapierWebhookCard } from '../../../components/ZapierWebhookCard';
import {
  DEFAULT_TRIGGER_NOTIFICATION_CONFIG,
  DEFAULT_ZAPIER_WEBHOOK_CONFIG,
  type TicketSettings,
  type TicketSettingsUpdateInput,
  type ZapierWebhookConfig,
  type ZapierWebhookEntry,
} from '@cp3/shared';

const SLA_KEYS: Array<keyof TicketSettings['slaHours']> = ['low', 'normal', 'high', 'urgent'];
const SLA_LABEL: Record<keyof TicketSettings['slaHours'], string> = {
  low: 'Niedrig',
  normal: 'Normal',
  high: 'Hoch',
  urgent: 'Dringend',
};

export function TicketSettingsPage() {
  const settings = useTicketSettings();
  const update = useUpdateTicketSettings();
  const { toast } = useToast();
  const [form, setForm] = React.useState<TicketSettingsUpdateInput | null>(null);

  React.useEffect(() => {
    if (settings.data && !form) {
      setForm({
        enabled: settings.data.enabled,
        recipientEmails: settings.data.recipientEmails,
        ccEmails: settings.data.ccEmails,
        highPriorityRecipientEmails: settings.data.highPriorityRecipientEmails,
        urgentPriorityRecipientEmails: settings.data.urgentPriorityRecipientEmails,
        replyToEmail: settings.data.replyToEmail,
        fromName: settings.data.fromName,
        customerOutboundEnabled: settings.data.customerOutboundEnabled,
        customerEmailSignature: settings.data.customerEmailSignature,
        autoConfirmCallerOnCreate: settings.data.autoConfirmCallerOnCreate ?? false,
        autoConfirmTemplateId: settings.data.autoConfirmTemplateId ?? '',
        ticketCreatedNotify:
          settings.data.ticketCreatedNotify ?? DEFAULT_TRIGGER_NOTIFICATION_CONFIG,
        appointmentBookedNotify:
          settings.data.appointmentBookedNotify ?? DEFAULT_TRIGGER_NOTIFICATION_CONFIG,
        zapierWebhooks:
          settings.data.zapierWebhooks ?? DEFAULT_ZAPIER_WEBHOOK_CONFIG,
        slaHours: settings.data.slaHours,
        escalationEnabled: settings.data.escalationEnabled,
        escalationAfterHours: settings.data.escalationAfterHours,
        escalationRecipientEmails: settings.data.escalationRecipientEmails,
      });
    }
  }, [settings.data, form]);

  if (settings.isLoading || !form) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Einstellungen …
      </div>
    );
  }

  function patch<K extends keyof TicketSettingsUpdateInput>(key: K, value: TicketSettingsUpdateInput[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function onSave() {
    if (!form) return;
    try {
      await update.mutateAsync(form);
      toast({ title: 'Ticket-Einstellungen gespeichert' });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={onSave} disabled={update.isPending}>
          {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Speichern
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="h-4 w-4" /> Generelle Einstellungen
          </CardTitle>
          <CardDescription>
            Ohne aktiven Schalter werden keine Tickets per E-Mail verschickt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SwitchRow
            label="Ticketing aktiv"
            description="Schalter aus = Sprach-KI legt weiterhin Tickets an, es werden aber keine E-Mails verschickt."
            checked={form.enabled ?? true}
            onChange={(v) => patch('enabled', v)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="fromName">Absendername</Label>
              <Input
                id="fromName"
                value={form.fromName ?? ''}
                onChange={(e) => patch('fromName', e.target.value)}
                placeholder="CP-3 Voice"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="replyTo">Antwort-an (Reply-To)</Label>
              <Input
                id="replyTo"
                type="email"
                value={form.replyToEmail ?? ''}
                onChange={(e) => patch('replyToEmail', e.target.value)}
                placeholder="support@deine-firma.de"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Empfänger</CardTitle>
          <CardDescription>
            Diese Adressen erhalten neue Tickets per E-Mail. Hochpriorisierte Tickets können separat geroutet werden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <EmailListField
            label="Standard-Empfänger (Standard, niedrig)"
            description="Hauptempfänger für jedes neue Ticket."
            value={form.recipientEmails ?? []}
            onChange={(v) => patch('recipientEmails', v)}
          />
          <EmailListField
            label="CC"
            description="In Kopie für jede Ticket-Mail."
            value={form.ccEmails ?? []}
            onChange={(v) => patch('ccEmails', v)}
          />
          <EmailListField
            label="Hohe Priorität — eigene Empfänger (optional)"
            description="Wenn leer, gilt die Standard-Liste."
            value={form.highPriorityRecipientEmails ?? []}
            onChange={(v) => patch('highPriorityRecipientEmails', v)}
          />
          <EmailListField
            label="Dringend — eigene Empfänger (optional)"
            description="Empfehlung: separater Verteiler / Pager / On-Call Adresse."
            value={form.urgentPriorityRecipientEmails ?? []}
            onChange={(v) => patch('urgentPriorityRecipientEmails', v)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SLAs</CardTitle>
          <CardDescription>
            Maximale Reaktionszeit pro Priorität (in Stunden). Daraus wird das Fälligkeitsdatum jedes neuen Tickets berechnet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-4">
            {SLA_KEYS.map((k) => (
              <div key={k} className="space-y-1.5">
                <Label>{SLA_LABEL[k]}</Label>
                <Input
                  type="number"
                  min={0}
                  max={720}
                  value={form.slaHours?.[k] ?? 0}
                  onChange={(e) =>
                    patch('slaHours', {
                      ...(form.slaHours ?? { low: 72, normal: 24, high: 4, urgent: 1 }),
                      [k]: Number(e.target.value),
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">Stunden bis Fälligkeit</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Eskalation</CardTitle>
          <CardDescription>
            Überfällige Tickets werden automatisch eskaliert. Der Scheduler läuft alle 15 Minuten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SwitchRow
            label="Auto-Eskalation aktiv"
            description="Schickt eine separate Mail, sobald ein Ticket die SLA überschritten hat."
            checked={form.escalationEnabled ?? true}
            onChange={(v) => patch('escalationEnabled', v)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Eskalation nach Überschreitung (Stunden)</Label>
              <Input
                type="number"
                min={0}
                max={168}
                value={form.escalationAfterHours ?? 2}
                onChange={(e) => patch('escalationAfterHours', Number(e.target.value))}
              />
            </div>
          </div>
          <EmailListField
            label="Eskalations-Empfänger"
            description="Wenn leer, geht die Eskalation an die Standard-Empfänger."
            value={form.escalationRecipientEmails ?? []}
            onChange={(v) => patch('escalationRecipientEmails', v)}
          />
        </CardContent>
      </Card>

      <NotificationTriggerCard
        title="Benachrichtigung bei Ticket-Erstellung"
        description="Wird ausgelöst, sobald die KI im Anruf ein Ticket angelegt hat. Pro Kanal Vorlage + Versand-Verzögerung wählen."
        value={form.ticketCreatedNotify}
        onChange={(v) => patch('ticketCreatedNotify', v)}
      />

      <ZapierWebhookCard
        title="Zapier-Webhook bei Ticket-Erstellung"
        description={
          'Schickt jedes neu angelegte Ticket als JSON an einen Zap. Damit kannst Du ' +
          'Tickets in Trello, Notion, Google Sheets, ein CRM oder beliebige andere ' +
          'Zapier-fähige Tools weiterleiten.'
        }
        trigger="ticketCreated"
        value={form.zapierWebhooks?.ticketCreated ?? DEFAULT_ZAPIER_WEBHOOK_CONFIG.ticketCreated}
        onChange={(next) =>
          patch('zapierWebhooks', mergeWebhook(form.zapierWebhooks, 'ticketCreated', next))
        }
        saving={update.isPending}
      />

      <ZapierWebhookCard
        title="Zapier-Webhook bei Ticket-Statusänderung"
        description={
          'Wird ausgelöst, wenn sich der Status eines Tickets ändert (z. B. open → ' +
          'in-progress, resolved, closed). Ideal für Statusspiegel in externen ' +
          'Systemen oder Slack-Updates.'
        }
        trigger="ticketStatusChanged"
        value={
          form.zapierWebhooks?.ticketStatusChanged ??
          DEFAULT_ZAPIER_WEBHOOK_CONFIG.ticketStatusChanged
        }
        onChange={(next) =>
          patch(
            'zapierWebhooks',
            mergeWebhook(form.zapierWebhooks, 'ticketStatusChanged', next),
          )
        }
        saving={update.isPending}
      />

      <Card>
        <CardHeader>
          <CardTitle>Antwort an Kunden</CardTitle>
          <CardDescription>
            Erlaubt es Bearbeiter*innen, einen Kommentar direkt per E-Mail an den Anrufer zu schicken.
            Die KI fragt im Anruf nach der E-Mail nur, wenn sie explizit genannt wird.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SwitchRow
            label="Kunden-Versand aktiv"
            description="Aktiviert die 'Per E-Mail an Kunden senden'-Option im Ticket-Detail."
            checked={form.customerOutboundEnabled ?? false}
            onChange={(v) => patch('customerOutboundEnabled', v)}
          />
          <SwitchRow
            label="Automatische Eingangsbestätigung"
            description={
              "Wenn die KI im Anruf eine E-Mail-Adresse erfasst hat, schickt das " +
              "System dem Anrufer sofort eine kurze Bestätigung, dass das Ticket " +
              "angekommen ist. Voraussetzung: 'Kunden-Versand aktiv' oben."
            }
            checked={form.autoConfirmCallerOnCreate ?? false}
            onChange={(v) => patch('autoConfirmCallerOnCreate', v)}
          />
          <div className="space-y-1.5">
            <Label htmlFor="signature">Signatur</Label>
            <Textarea
              id="signature"
              rows={4}
              value={form.customerEmailSignature ?? ''}
              onChange={(e) => patch('customerEmailSignature', e.target.value)}
              placeholder="Mit freundlichen Grüßen&#10;Dein Team"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Merges a single trigger update into the full ZapierWebhookConfig
 * shape, preserving the other two triggers. Pulled out of the JSX so
 * the card props stay readable.
 */
function mergeWebhook(
  current: ZapierWebhookConfig | undefined,
  trigger: keyof ZapierWebhookConfig,
  next: ZapierWebhookEntry,
): ZapierWebhookConfig {
  const base = current ?? DEFAULT_ZAPIER_WEBHOOK_CONFIG;
  return { ...base, [trigger]: next };
}

function SwitchRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="font-medium">{label}</div>
        {description && <div className="text-xs text-muted-foreground">{description}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function EmailListField({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [draft, setDraft] = React.useState('');
  function add() {
    const v = draft.trim();
    if (!v) return;
    if (!/.+@.+\..+/.test(v)) return;
    if (value.includes(v)) {
      setDraft('');
      return;
    }
    onChange([...value, v]);
    setDraft('');
  }
  return (
    <div className="space-y-2">
      <div>
        <Label>{label}</Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs"
          >
            {email}
            <button
              type="button"
              onClick={() => onChange(value.filter((e) => e !== email))}
              className="text-muted-foreground hover:text-destructive"
              aria-label={`Entfernen: ${email}`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
        {value.length === 0 && (
          <span className="text-xs text-muted-foreground">Noch keine Empfänger.</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="name@firma.de"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Hinzufügen
        </Button>
      </div>
    </div>
  );
}
